import { maybe } from '@passionware/monads';
import type { BookingBilling } from 'v5/core/booking-billing/model';
import type { ExpenseLine } from 'v5/core/expense-billing/model';
import type { CurrencyCode } from 'v5/core/money/model';
import type {
  BillingAssignment,
  BillingExpenseLine,
  BillingSection,
  BillingSlot,
  ResourcingBillingTree,
  ResourcingSlotKind,
  ResourceSlotPlan
} from './billing-tree';

export type ResourcingBillingGroupInput = {
  id: number;
  name: string;
  order: number;
  archived?: boolean;
};

export type ResourcingBillingResourceSlotInput = {
  id: number;
  groupId: number;
  order: number;
  note?: string | null;
  slotCategoryId?: number | null;
  includeInTotals: boolean;
  archived?: boolean;
  plan?: ResourceSlotPlan;
  assignments: BillingAssignment[];
};

export type ResourcingBillingExpenseSlotInput = {
  id: number;
  groupId: number;
  order: number;
  note?: string | null;
  slotCategoryId?: number | null;
  includeInTotals: boolean;
  archived?: boolean;
  plan?: ResourceSlotPlan;
  lines: BillingExpenseLine[];
};

export type ResourcingBillingTreeInput = {
  productionId: number;
  presentationCurrency: CurrencyCode;
  groups: ResourcingBillingGroupInput[];
  resourceSlots: ResourcingBillingResourceSlotInput[];
  expenseSlots: ResourcingBillingExpenseSlotInput[];
};

export function buildResourcingBillingTree(
  input: ResourcingBillingTreeInput
): ResourcingBillingTree {
  const groups = input.groups
    .filter(g => !g.archived)
    .sort((a, b) => a.order - b.order || a.id - b.id);

  const resourceSlots = input.resourceSlots.filter(s => !s.archived);
  const expenseSlots = input.expenseSlots.filter(s => !s.archived);

  const sections: BillingSection[] = groups.map(group => {
    const groupResourceSlots = resourceSlots
      .filter(s => s.groupId === group.id)
      .sort((a, b) => a.order - b.order || a.id - b.id);
    const groupExpenseSlots = expenseSlots
      .filter(s => s.groupId === group.id)
      .sort((a, b) => a.order - b.order || a.id - b.id);

    const slots: BillingSlot[] = [];

    for (const slot of groupResourceSlots) {
      slots.push({
        id: String(slot.id),
        kind: 'resource' satisfies ResourcingSlotKind,
        slotId: slot.id,
        groupId: slot.groupId,
        order: slot.order,
        note: maybe.of(slot.note ?? null),
        slotCategoryId: maybe.of(slot.slotCategoryId ?? null),
        includeInTotals: slot.includeInTotals,
        plan:
          slot.assignments.length === 0 && slot.plan
            ? maybe.of(slot.plan)
            : maybe.ofAbsent(),
        assignments: slot.assignments,
        lines: []
      });
    }

    for (const expenseSlot of groupExpenseSlots) {
      const hasLines = expenseSlot.lines.length > 0;
      slots.push({
        id: `expense-${expenseSlot.id}`,
        kind: 'expense' satisfies ResourcingSlotKind,
        slotId: expenseSlot.id,
        groupId: expenseSlot.groupId,
        order: expenseSlot.order,
        note: maybe.of(expenseSlot.note ?? null),
        slotCategoryId: maybe.of(expenseSlot.slotCategoryId ?? null),
        includeInTotals: expenseSlot.includeInTotals,
        plan:
          !hasLines && expenseSlot.plan
            ? maybe.of(expenseSlot.plan)
            : maybe.ofAbsent(),
        assignments: [],
        lines: expenseSlot.lines
      });
    }

    return {
      id: String(group.id),
      name: group.name,
      order: group.order,
      includeInTotals: true,
      slots
    };
  });

  return {
    productionId: input.productionId,
    presentationCurrency: input.presentationCurrency,
    sections
  };
}

/** Re-export for adapters that build assignments from booking billing. */
export type { BookingBilling, ExpenseLine };
